import { Link } from '@tanstack/react-router';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { Button, MonoLabel, buttonClassName } from '@/component';
import { useFriendActions, type Relation } from '@/hook';

interface RelationActionProps {
  userId: Id<'users'>;
  relation: Relation;
  /** sm on list rows, md on the profile header. */
  size?: 'sm' | 'md';
}

/** The one button that says where you stand with someone and moves it on:
 * Add, Accept, Requested, Friends (click to remove), Unblock on the Blocked
 * list, or Edit profile for you. */
export function RelationAction({ userId, relation, size = 'sm' }: RelationActionProps) {
  const actions = useFriendActions();
  const md = size === 'md';
  switch (relation.state) {
    case 'self':
      return (
        <Link to="/settings" className={buttonClassName({ variant: 'outline', size })}>
          Edit profile
        </Link>
      );
    case 'blocked':
      return (
        <Button variant="outline" size={size} quiet onClick={() => void actions.unblock(userId)}>
          Unblock
        </Button>
      );
    case 'friends':
      return (
        <Button
          variant="outline"
          size={size}
          onClick={() => {
            if (window.confirm('Remove this friend? You can ask again later.')) void actions.remove(userId);
          }}
        >
          Friends ✓
        </Button>
      );
    case 'requested':
      return <MonoLabel tone="mute">Requested</MonoLabel>;
    case 'incoming':
      return (
        <span className="flex items-center gap-2">
          <Button size={size} onClick={() => void actions.accept(relation.requestId)}>
            {md ? 'Accept request' : 'Accept'}
          </Button>
          <Button variant="text" size={size} quiet onClick={() => void actions.decline(relation.requestId)}>
            Decline
          </Button>
        </span>
      );
    case 'none':
      return (
        <Button size={size} onClick={() => void actions.request(userId)}>
          {md ? 'Add friend' : 'Add'}
        </Button>
      );
  }
}
